import React from "react";
import { SectionCard, Pill } from "../Ui";
import { PADDY_LAB_METRICS, formatLabLimitHint, formatPaddyLabMetric } from "../../lib/paddyLabConfig";
import { formatListDateTime } from "../../lib/purchaseListMeta";
import { hasRole, isAdmin, useAuth } from "../../context/AuthContext";
import DocumentPrintPdfActions from "../DocumentPrintPdfActions";

function metricSuffix(key) {
  return PADDY_LAB_METRICS.find((m) => m.key === key)?.suffix || "%";
}

function formatCell(metric, phaseKey, lab) {
  const val = metric.values?.[phaseKey];
  if (val == null || val === "") return "—";
  const suffix = metric.suffix || metricSuffix(metric.key);
  const fakeLab = { [metric.key]: val };
  const m = PADDY_LAB_METRICS.find((x) => x.key === metric.key) || { key: metric.key, suffix };
  return formatPaddyLabMetric(fakeLab, m);
}

function cellClass(metric, phaseKey) {
  if (!Array.isArray(metric.flagged_phases)) return "";
  return metric.flagged_phases.includes(phaseKey) ? "text-red-600 font-semibold" : "";
}

export default function PurchaseLabComparison({ purchase, comparison, labLimits = null }) {
  const { user } = useAuth();
  const canSeeVerdict = isAdmin(user) || hasRole(user, "manager");

  if (!comparison?.phases?.length) {
    return null;
  }

  const phases = comparison.phases;
  const metrics = comparison.metrics?.length
    ? comparison.metrics
    : PADDY_LAB_METRICS.map((m) => ({
        key: m.key,
        label: m.label,
        suffix: m.suffix,
        values: Object.fromEntries(
          phases.map((ph) => [ph.key, ph.lab?.[m.key]]),
        ),
        flagged_phases: [],
      }));

  const label = purchase?.vehicle_number || purchase?.code || purchase?.id;
  const safeLabel = String(label).replace(/[/\\?%*:|"<>]/g, "-");

  return (
    <SectionCard
      title={comparison.comparable ? "Lab analysis comparison" : "Lab analysis"}
      testId="purchase-lab-comparison"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-xs text-muted-foreground">
          {comparison.comparable
            ? "Pre-unloading, relab, and composite readings side by side for the same truck."
            : "Quality readings recorded for this purchase."}
        </p>
        <DocumentPrintPdfActions
          printPath={`/purchases/${purchase.id}/lab-comparison/receipt/print`}
          pdfPath={`/purchases/${purchase.id}/lab-comparison/receipt/pdf`}
          pdfFilename={`lab-comparison-${safeLabel}.pdf`}
          printTitle="Print lab comparison"
          pdfTitle="Download lab comparison PDF"
          testIdPrefix={`lab-comparison-${purchase.id}`}
          pdfSuccessMessage="Comparison PDF downloaded"
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
              <th className="px-3 py-2.5">Parameter</th>
              {phases.map((ph) => (
                <th key={ph.key} className="px-3 py-2.5 whitespace-nowrap">
                  <div>{ph.label}</div>
                  {ph.created_at ? (
                    <div className="text-[9px] font-normal normal-case text-muted-foreground mt-0.5">
                      {formatListDateTime(ph.created_at)}
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric) => {
              const hint = labLimits
                ? formatLabLimitHint(labLimits, metric.key, (metric.suffix || "%").trim() || "%")
                : metric.limit
                  ? formatLabLimitHint({ [metric.key]: metric.limit }, metric.key, (metric.suffix || "%").trim() || "%")
                  : null;
              return (
                <tr key={metric.key} className="border-t border-border">
                  <td className="px-3 py-2.5 text-xs">
                    <div>{metric.label}</div>
                    {hint ? <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div> : null}
                  </td>
                  {phases.map((ph) => (
                    <td
                      key={`${metric.key}-${ph.key}`}
                      className={`px-3 py-2.5 text-xs tabular-nums ${cellClass(metric, ph.key)}`}
                    >
                      {formatCell(metric, ph.key, ph.lab)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canSeeVerdict && (
        <div className="mt-3 flex flex-wrap gap-2">
          {phases.map((ph) => (
            <div key={ph.key} className="text-xs flex items-center gap-2">
              <span className="text-muted-foreground">{ph.label}:</span>
              {ph.needs_admin_review ? (
                <Pill tone="warning">Needs review</Pill>
              ) : ph.verdict ? (
                <Pill tone={["RECORDED", "AUTO_PASS", "PASS", "APPROVED"].includes(ph.verdict) ? "success" : "warning"}>
                  {(ph.verdict || "Recorded").replace(/_/g, " ")}
                </Pill>
              ) : (
                <span className="text-muted-foreground">Recorded</span>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
